import { weatherApi } from './weatherApi';

export interface LocationSuggestion {
  display_name: string;
  name: string;
  state?: string;
  country: string;
  lat: number;
  lon: number;
  type: string; // city, town, village, etc.
  importance: number;
}

class LocationSearchService {
  private cache = new Map<string, LocationSuggestion[]>();
  private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
  private cacheTimestamps = new Map<string, number>();

  async searchLocations(query: string): Promise<LocationSuggestion[]> {
    if (!query || query.length < 2) {
      return [];
    }

    const normalizedQuery = query.toLowerCase().trim();
    
    // Check cache first
    if (this.isCacheValid(normalizedQuery)) {
      const cached = this.cache.get(normalizedQuery);
      if (cached) {
        return cached;
      }
    }

    try {
      console.log('Searching for location:', query); // Debug log
      
      // Use backend API that searches GSOD weather stations
      const response = await weatherApi.api.get('/weather/locations/search', {
        params: { q: query }
      });

      if (response.data.success) {
        const suggestions = response.data.data as LocationSuggestion[];
        console.log('Backend location search results:', suggestions); // Debug log

        // Cache the results
        this.cache.set(normalizedQuery, suggestions);
        this.cacheTimestamps.set(normalizedQuery, Date.now());

        return suggestions;
      } else {
        console.warn('Backend location search failed:', response.data.error);
        return [];
      }
    } catch (error) {
      console.error('Location search error:', error);
      return [];
    }
  }

  private isCacheValid(query: string): boolean {
    const timestamp = this.cacheTimestamps.get(query);
    if (!timestamp) return false;
    
    return Date.now() - timestamp < this.CACHE_DURATION;
  }

  // Validate a location by checking if it can be geocoded
  async validateLocation(locationName: string): Promise<LocationSuggestion | null> {
    const suggestions = await this.searchLocations(locationName);
    
    // Look for exact or very close matches
    const exactMatch = suggestions.find(s => 
      s.display_name.toLowerCase() === locationName.toLowerCase() ||
      s.name.toLowerCase() === locationName.toLowerCase()
    );
    
    if (exactMatch) return exactMatch;
    
    // Look for close matches (first result if importance is high enough)
    if (suggestions.length > 0 && suggestions[0].importance > 0.3) {
      return suggestions[0];
    }
    
    return null;
  }
}

export const locationSearchApi = new LocationSearchService();
